<template>
  <div class="relative w-full rounded-[28px] p-7 sm:p-10 space-y-6 overflow-hidden
         border border-white/10
         bg-white/70 dark:bg-white/[0.06]
         backdrop-blur-2xl
         shadow-[0_30px_90px_rgba(0,0,0,0.35)]">

    <!-- highlight superior tipo Apple -->
    <div class="pointer-events-none absolute inset-0 rounded-[28px] overflow-hidden">
      <div class="absolute inset-0 opacity-[0.9]" style="background:
        radial-gradient(circle at 30% 18%, rgba(255,255,255,0.55), transparent 55%),
        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.10), transparent 60%),
        linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.04));
      ">
      </div>
    </div>

    <div class="relative">
      <!-- Header -->
      <div class="flex flex-col items-center text-center gap-3 mb-6">
        <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-elegant-gray-dark dark:text-white">
          {{ showPasswordField ? "Iniciar sesión" : "Acceso con Magic Link" }}
        </h1>

        <p class="text-sm text-elegant-gray-medium dark:text-silver-300/80 leading-relaxed">
          <span v-if="showPasswordField">
            Ingresa con tu correo y contraseña para acceder a tu cuenta.
          </span>
          <span v-else>
            Te enviaremos un enlace seguro a tu correo para iniciar sesión sin contraseña.
          </span>
        </p>
      </div>

      <!-- Form -->
      <div role="form" class="space-y-5">
        <!-- Email -->
        <div class="space-y-1">
          <label class="text-xs font-extrabold tracking-wide text-elegant-gray-dark/80 dark:text-silver-200" for="email">
            Correo electrónico
          </label>

          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-silver-500 dark:text-white/40 material-symbols-outlined text-[18px]">
              mail
            </span>

            <input id="email" type="email" v-model="credentials.email" @keyup.enter="validate" class="w-full rounded-xl border text-sm pl-11 pr-3 py-3.5 outline-none transition
                     bg-white dark:bg-surface-dark
                     border-silver-200 dark:border-white/10
                     text-elegant-gray-dark dark:text-silver-100
                     placeholder:text-silver-500 dark:placeholder:text-silver-700
                     focus:ring-2 focus:ring-primary/40 focus:border-primary" :class="{ 'input-error': errors.email }"
              placeholder="tu@correo.com" name="email" />
          </div>

          <p v-if="errors.email" class="text-xs text-red-500 font-semibold">
            {{ errors.email }}
          </p>
        </div>

        <!-- Password -->
        <transition name="fade" mode="out-in">
          <div v-if="options.password.enabled && showPasswordField" class="space-y-1">
            <label class="text-xs font-extrabold tracking-wide text-elegant-gray-dark/80 dark:text-silver-200"
              for="password">
              Contraseña
            </label>

            <div class="relative">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-silver-500 dark:text-white/40 material-symbols-outlined text-[18px]">
                lock
              </span>

              <input id="password" :type="showPassword ? 'text' : 'password'" v-model="credentials.password" @keyup.enter="validate" class="w-full rounded-xl border text-sm pl-11 pr-11 py-3.5 outline-none transition
                       bg-white dark:bg-surface-dark
                       border-silver-200 dark:border-white/10
                       text-elegant-gray-dark dark:text-silver-100
                       placeholder:text-silver-500 dark:placeholder:text-silver-700
                       focus:ring-2 focus:ring-primary/40 focus:border-primary"
                :class="{ 'input-error': errors.password }" placeholder="••••••••" name="password" />

              <button type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-silver-500 dark:text-white/40 hover:text-elegant-gray-dark dark:hover:text-white hover:bg-silver-100 dark:hover:bg-white/10 transition"
                :title="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click.prevent="showPassword = !showPassword">
                <span class="material-symbols-outlined text-[18px] block">
                  {{ showPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </button>
            </div>

            <p v-if="errors.password" class="text-xs text-red-500 font-semibold">
              {{ errors.password }}
            </p>
          </div>
        </transition>

        <!-- CTA -->
        <button class="w-full rounded-full px-5 py-3.5 font-extrabold text-sm inline-flex items-center justify-center gap-2 transition
                 bg-primary text-white hover:brightness-110 active:brightness-95
                 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_14px_40px_rgba(11,129,239,0.25)]"
          :disabled="loading" @click.prevent="validate">

          <span>{{ showPasswordField ? "Iniciar sesión" : "Enviar Magic Link" }}</span>

          <span v-if="loading" class="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
        </button>

        <!-- Switch helper -->
        <div class="text-center text-xs sm:text-sm text-elegant-gray-medium dark:text-white/70">
          <template v-if="showPasswordField">
            <div class="flex flex-col items-center gap-2">
              <span class="text-[11px] uppercase tracking-widest opacity-70">o</span>
              <button class="inline-flex items-center gap-1 font-bold text-primary hover:opacity-80 transition"
                @click.prevent="setAuthMethod('magic')">
                Recibir magic link
                <span class="material-symbols-outlined text-base">arrow_forward</span>
              </button>
            </div>
          </template>

          <template v-else>
            <button
              class="inline-flex items-center gap-2 font-extrabold uppercase tracking-widest text-primary hover:opacity-80 transition text-[11px]"
              @click.prevent="setAuthMethod('password')">
              <span class="material-symbols-outlined text-base">arrow_back</span>
              Regresar a contraseña
            </button>
          </template>
        </div>

        <!-- Register -->
        <div v-if="allowRegister"
          class="rounded-2xl border border-silver-200 dark:border-white/10 dark:border-white/10 bg-white/60 dark:bg-white/5 px-4 py-3 text-center">
          <p class="text-xs sm:text-sm text-elegant-gray-dark/80 dark:text-silver-200">
            ¿No tienes cuenta?
            <a href="/register"
              class="ml-1 inline-flex items-center gap-1 font-extrabold text-primary hover:opacity-80 transition">
              Regístrate
              <span class="material-symbols-outlined text-base">arrow_forward</span>
            </a>
          </p>
          <p class="mt-1 text-[11px] text-elegant-gray-medium dark:text-white/60">
            Crea tu cuenta en minutos y comienza a usar Finova.
          </p>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import Validators from "./validators";

export default {
  mixins: [Validators],
  inject: ["$axios"],
  props: {
    options: { type: Object, required: true },
    userType: { type: String, required: true },
    allowRegister: { type: Boolean, required: true },
    logoSrc: { type: String, default: "./finova-logo.jpeg" } // usa tu asset
  },
  data: () => ({
    showPasswordField: true,
    showPassword: false,
    loading: false
  }),
  computed: {
    url() {
      return this.showPasswordField ? this.options?.password?.url : this.options?.magicLink?.url;
    },
    type() {
      return this.showPasswordField ? "password" : "magic link";
    }
  },
  methods: {
    setAuthMethod(method) {
      this.resetErrors();

      const isPassword = method === "password";
      this.showPasswordField = isPassword;

      localStorage.setItem("login.pref_auth_method", isPassword ? "password" : "magic");
    },
    resetErrors() {
      this.errors = {};
    },
    validate() {
      this.resetErrors();

      this.validateFormat("email", "email");
      if (this.type === "password") this.validateField("password");

      if (Object.keys(this.errors).length === 0) this.submit();
    },
    async submit() {
      if (this.loading) return;
      this.loading = true;

      const dataToSend =
        this.type === "magic link"
          ? { data: { email: this.credentials.email } }
          : { [this.userType]: { ...this.credentials } };

      if (!this.url) {
        console.error("No api configured for auth request. options example:", {
          magicLink: { enabled: true, url: "api_url" },
          password: { enabled: true, url: "api_url" }
        });
        this.loading = false;
        return;
      }

      this.$axios
        .post(this.url, dataToSend)
        .then((response) => {
          const status = response.data;
          if (status.code >= 200 && status.code < 300) {
            this.$emit("onSubmit", { ...status, type: this.type, credentials: this.credentials });
            this.resetErrors();
          } else {
            this.$emit("onError", status);
          }
        })
        .catch((e) => this.$emit("onError", e))
        .finally(() => {
          this.loading = false;
        });
    }
  },
  validations() {
    return this.credentialsRules;
  },
  created() {
    const pref = localStorage.getItem("login.pref_auth_method");
    // default: password (si no hay preferencia)
    this.showPasswordField = (pref || "password") === "password";
  }
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.input-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.18);
}
</style>